import React from 'react';
import PropTypes from 'prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  Button,
  Card,
  Link,
  ProgressBar,
  Typography,
} from '@one-thd/sui-atomic-components';
import Markdown from 'react-markdown';
import {
  windowExists,
  isAuthenticated,
  gotoLogonModule,
  handleSignOutSignIn,
} from '../../../../common/utils/AccountUtils';
import { useImageError } from '../../utils/SweepstakesUtils';
import { useSweepstakesData } from '../../../../contexts/SweepstakesDataContext';
import { SWEEPSTAKES_MOCK_DATA } from '../../../../core/Data/SweepstakesMockData';
import { SWEEPSTAKES_CONSTANTS, GenericErrorMessage, DISPLAY_PROGRESS_STATUS } from '../../../../core/Constants';
import {
  analyticsSweepstakesWelcomeCard,
  analyticsSweepstakesPostPageError,
} from '../../../../common/utils/AnalyticsUtils';
import {
  SWEEPSTAKES_NR_CTA,
  trackSweepstakesCtaClick,
} from '../../../../common/utils/NewRelic';
import { fireDrawerOpenButtonAnalytics } from '../../utils/SweepstakesAnalyticsUtils';
import { useResponsive } from '../../../../hooks';
import { mapSweepstakesData } from '../../utils/SweepstakesDataMapper';
import {
  RenderGenericError,
  RenderSweepstakesCardLoading,
} from '../SweepstakesUtils';
import { getImageURL, preloadImage } from '../../../../common/utils/CardUtils';
import { SweepstakesTitle } from '../../utils/SweepstakesTitle';

// Custom palette overrides for the sweepstakes progress bar.
// Brand orange (#F96302) for in-progress, light green (#9CE8C4) for complete.
// Applied via inline style to guarantee specificity across all consuming apps,
// since ThemeProvider/createTheme can be overridden by host app theme variables.
const progressBarColorOverrides = {
  '--sui-palette-background-warning-strong': '249 99 2',
  '--sui-palette-background-success-strongest': '156 232 196',
};

// Utility functions
const getHeaderClasses = (showOutsideHeaderOn, showInsideHeaderOn) => {
  const buildClasses = (breakpoints) => {
    if (!breakpoints || (Array.isArray(breakpoints) && breakpoints.length === 0)) {
      return 'sui-hidden';
    }

    const bps = Array.isArray(breakpoints) ? breakpoints : [breakpoints];
    const hasMW = bps.includes('MW');
    const hasTW = bps.includes('TW');
    const hasDT = bps.includes('DT');

    const classes = [];

    // Set default mobile visibility
    if (hasMW) {
      classes.push('sui-block');
    } else {
      classes.push('sui-hidden');
    }

    // Set tablet visibility (sm breakpoint)
    if (hasTW) {
      classes.push('sm:sui-block');
    } else {
      classes.push('sm:sui-hidden');
    }

    // Set desktop visibility (lg breakpoint)
    if (hasDT) {
      classes.push('lg:sui-block');
    } else {
      classes.push('lg:sui-hidden');
    }

    return classes.join(' ');
  };

  return {
    outside: buildClasses(showOutsideHeaderOn),
    inside: buildClasses(showInsideHeaderOn),
  };
};
// Render components
const RenderLogo = ({ logoUrl, logoFallback, imgRetryCounter }) => {
  if (!logoUrl) return null;

  return logoFallback.hasError ? (
    <img
      src={SWEEPSTAKES_CONSTANTS.URLS.LOGO_FALLBACK}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      data-testid="sweepstakes-logo-fallback"
    />
  ) : (
    <img
      key={logoUrl + imgRetryCounter}
      src={logoUrl}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      onError={logoFallback.handleError}
      data-testid="sweepstakes-logo"
    />
  );
};

const RenderTitle = ({ title, subtitle }) => {
  if (!title || !subtitle) return null;

  return (
    <SweepstakesTitle
      line1={title}
      line2={subtitle}
      inverse
      variant="card"
      testIdPrefix="sweepstakes"
    />
  );
};

const RenderDescription = ({ description }) => {
  if (!description) return null;

  return (
    <div data-testid="sweepstakes-description">
      <Typography color="inverse" variant="body-sm">
        {description}
      </Typography>
    </div>
  );
};

const RenderTermsAndConditions = ({ terms }) => {
  if (!terms) return null;

  return (
    // TODO: follow up, why can't primary be used here in place of #FFFFFF
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <div className="sui-bg-[#FFFFFF] sui-rounded-sm sui-p-2 sui-mt-3" data-testid="sweepstakes-terms-and-conditions">
      <Markdown
        renderers={{
          link: ({ href, children }) => (
            <Link color="brand" href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </Link>
          ),
        }}
      >
        {terms}
      </Markdown>
    </div>
  );
};

const RenderSignInButton = ({ isPurchaser, onClick }) => (
  <div className="sui-pt-2">
    <Button
      variant="primary"
      size="small"
      onClick={onClick}
      fullWidth
      data-testid="sweepstakes-sign-in-button"
    >
      {SWEEPSTAKES_CONSTANTS.AUTH_CTA_TEXT(isPurchaser)}
    </Button>
  </div>
);

const RenderGetStartedButton = ({ onClick, label }) => (
  <div className="sui-pt-2">
    <Button
      variant="primary"
      size="small"
      onClick={onClick}
      fullWidth
      data-testid="sweepstakes-get-started-button"
    >
      {label || 'Get Started'}
    </Button>
  </div>
);

const RenderCtaSection = ({
  isAuthenticated: isAuthed, activityStatus, terms, onGetStarted,
  isPurchaser: isPurchaserUser,
  onAuthClick,
  getStartedLabel,
  isOptedIn,
  step1Pending,
}) => {
  const userIsOptedIn = isOptedIn
    || step1Pending
    || activityStatus === 'IN_PROGRESS'
    || activityStatus === 'OPTED_IN';
  const isPreOptIn = !isAuthed
    || activityStatus === 'ELIGIBLE'
    || activityStatus === 'INVITED';

  // Show CTA only for: purchasers OR (pre-opt-in AND not opted-in)
  const showCta = isPurchaserUser || (isPreOptIn && !userIsOptedIn);
  if (!showCta) return null;

  const showSignIn = !isAuthed || isPurchaserUser;

  return (
    <>
      {showSignIn
        ? <RenderSignInButton isPurchaser={isPurchaserUser} onClick={onAuthClick} />
        : <RenderGetStartedButton onClick={onGetStarted} label={getStartedLabel} />}
      <RenderTermsAndConditions terms={terms} />
    </>
  );
};

const RenderProgressSection = ({ sweepstakesData, onCompletedCta, isExpiredIncomplete }) => {
  if (!sweepstakesData?.isOptedIn) return null;

  const { completedSteps = 0, totalSteps = 0 } = sweepstakesData;
  const percentComplete = totalSteps === 0
    ? 0
    : Math.round((completedSteps / totalSteps) * 100);
  const isComplete = percentComplete === 100;

  const btnText = (isComplete || isExpiredIncomplete)
    ? sweepstakesData.completedCta
    : sweepstakesData.inProgressCta;
  const progressStatus = isComplete ? 'success' : 'warning';
  const labelText = isComplete
    ? (sweepstakesData.messageComplete || '').replace(/\*/g, '')
    : `${completedSteps} of ${totalSteps} Earned`;

  const handleOpenDrawer = () => {
    if (!windowExists()) return;
    trackSweepstakesCtaClick(
      isComplete || isExpiredIncomplete
        ? SWEEPSTAKES_NR_CTA.ENTRY_CARD_REVIEW_ENTRIES
        : SWEEPSTAKES_NR_CTA.ENTRY_CARD_EARN_MORE_ENTRIES
    );
    analyticsSweepstakesWelcomeCard(btnText.toLowerCase());
    const currentPath = window.location.pathname;
    const isLandingPage = currentPath.includes('fifa-world-cup-2026')
      && !currentPath.includes('-sweepstakes');

    if (!isLandingPage && (isComplete || isExpiredIncomplete)) {
      if (onCompletedCta) {
        onCompletedCta(sweepstakesData);
      } else {
        window.location.href = SWEEPSTAKES_CONSTANTS.PATHS.LANDING_PAGE;
      }
    } else {
      fireDrawerOpenButtonAnalytics(
        SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS, null, sweepstakesData?.steps
      );
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
        'loyalty-benefits.open-sweepstakes-details-drawer',
        { sweepstakesData }
      );
    }
  };

  return (
    <div className="sui-mt-4" data-testid="sweepstakes-progress-section">
      {!isExpiredIncomplete && (
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        <div className="sui-p-3 sui-bg-[#FFFFFF] sui-rounded-lg" style={progressBarColorOverrides}>
          <ProgressBar
            disableStatusIcon
            status={progressStatus}
            startLabel={
              isComplete
                ? <Typography variant="body-base" weight="bold">{labelText}</Typography>
                : labelText
            }
            value={percentComplete}
            size="large"
          />
        </div>
      )}
      <div className="sui-pt-2 sui-mt-2">
        <Button
          data-testid="sweepstakes-entries-button"
          variant="primary"
          size="small"
          onClick={handleOpenDrawer}
          fullWidth
        >
          {btnText}
        </Button>
      </div>
    </div>
  );
};

const RenderHeaderLink = ({ href, openNewWindow }) => (
  <a
    href={href}
    target={openNewWindow ? '_blank' : '_self'}
    rel={openNewWindow ? 'noopener noreferrer' : undefined}
    className="sui-cursor-pointer sui-underline"
    data-testid="sweepstakes-faqs-link"
    aria-label="View sweepstakes frequently asked questions"
  >
    Sweeps FAQs
  </a>
);

const RenderHeaderButton = ({ onClick }) => (
  <button
    type="button"
    className="sui-cursor-pointer sui-underline"
    onClick={onClick}
    data-testid="sweepstakes-faqs-link"
    aria-label="View sweepstakes frequently asked questions (opens in new tab)"
  >
    Sweeps FAQs
  </button>
);

const RenderHeader = ({ href, onClick, openNewWindow }) => (
  <div className="sui-flex sui-items-center sui-justify-between sui-mb-4">
    <Typography variant="h5" data-testid="sweepstakes-header-title">
      Sweepstakes Available (1)
    </Typography>
    <div>
      {href && <RenderHeaderLink href={href} openNewWindow={openNewWindow} />}
      {!href && onClick && <RenderHeaderButton onClick={onClick} />}
    </div>
  </div>
);

const RenderErrorCard = ({ refresh }) => {
  const testIdStr = 'sweepstakes-entry-card-error';
  return (
    <Card grow>
      <RenderGenericError refresh={refresh} testId={testIdStr} />
    </Card>
  );
};

const RenderLoadingCard = () => (
  <Card grow data-testid="sweepstakes-entry-card">
    <RenderSweepstakesCardLoading />
  </Card>
);

const unauthenticatedData = (isAuth) => {
  return (windowExists() && !isAuth ? mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.unauthenticated) : null);
};

const purchaserData = (isPurchaserUser) => {
  return isPurchaserUser ? mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.purchaser) : null;
};

export const SweepstakesEntryCard = ({ SweepstakesEntryCardHeaderProps, onCompletedCta, impressionData }) => {
  const {
    sweepstakesData, isLoading, hasError, refresh, isPurchaser, forceUnauthenticated
  } = useSweepstakesData();
  const { ref: impressionRef, clickID } = useImpression(impressionData ? { data: impressionData } : undefined);
  const { isMobile } = useResponsive();
  const isAuth = isAuthenticated();

  // When forceUnauthenticated is true, treat the user as unauthenticated
  // regardless of actual JWT presence (used for local demo toggling)
  const effectivelyAuthenticated = !forceUnauthenticated && isAuthenticated();

  // Fallback chain: API data → unauthenticated mock → purchaser mock → generic fallback
  // Null-role authenticated users fall through to the unauthenticated mock
  const displayData = sweepstakesData
    || unauthenticatedData(isAuth)
    || purchaserData(isPurchaser)
    || unauthenticatedData(false);

  const logoFallback = useImageError();

  // Preload the opt-in drawer background image so it is cached
  // before the user opens the drawer, eliminating the flash.
  React.useEffect(() => {
    const bgImage = displayData?.preOptInDrawerBackgroundImage;
    preloadImage(getImageURL(bgImage || 'sweepstakes-field-bg.jpeg'));
  }, [displayData?.preOptInDrawerBackgroundImage]);

  const [imgRetryCounter, setImgRetryCounter] = React.useState(0);
  React.useEffect(() => {
    if (imgRetryCounter === 0) setImgRetryCounter((prev) => prev + 1);
  }, [imgRetryCounter]);

  const errorFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (!effectivelyAuthenticated || !hasError || errorFiredRef.current) return;
    errorFiredRef.current = true;
    analyticsSweepstakesPostPageError({
      type: 'system',
      displayType: 'inline',
      pageSection: 'fifa sweepstakes',
      message: GenericErrorMessage,
      code: 'n/a',
    });
  }, [effectivelyAuthenticated, hasError]);

  const handleAuthClick = () => {
    trackSweepstakesCtaClick(
      isPurchaser
        ? SWEEPSTAKES_NR_CTA.ENTRY_CARD_PURCHASER_SIGN_IN
        : SWEEPSTAKES_NR_CTA.ENTRY_CARD_UNAUTH_SIGN_IN
    );
    analyticsSweepstakesWelcomeCard(SWEEPSTAKES_CONSTANTS.AUTH_CTA_TEXT(isPurchaser).toLowerCase());
    const authFn = isPurchaser ? handleSignOutSignIn : gotoLogonModule;
    authFn();
  };

  const handleDetailsClick = React.useCallback((event) => {
    event.stopPropagation();
    if (isLoading || hasError || !windowExists() || !displayData) {
      return;
    }
    trackSweepstakesCtaClick(SWEEPSTAKES_NR_CTA.ENTRY_CARD_GET_STARTED);
    analyticsSweepstakesWelcomeCard((displayData?.getStartedCta || 'Get Started').toLowerCase());

    // Determine which drawer to open based on sweepstakes status
    if (displayData.isOptedIn) {
      const completedSteps = displayData.completedSteps || 0;

      // If the user is opted in but hasn't completed any steps yet,
      // the expected experience is to start the first step (survey).
      if (completedSteps === 0) {
        fireDrawerOpenButtonAnalytics(
          SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY, null, displayData?.steps
        );
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-survey-drawer', {
          sweepstakesData: displayData,
        });
        return;
      }

      fireDrawerOpenButtonAnalytics(
        SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS, null, displayData?.steps
      );
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-details-drawer', {
        sweepstakesData: displayData,
      });
    } else {
      fireDrawerOpenButtonAnalytics(
        SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN, null, displayData?.steps
      );
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-opt-in-drawer', {
        sweepstakesData: displayData,
      });
    }
  }, [isLoading, hasError, displayData]);

  const headerClasses = SweepstakesEntryCardHeaderProps
    ? getHeaderClasses(
      SweepstakesEntryCardHeaderProps.showOutsideHeaderOn,
      SweepstakesEntryCardHeaderProps.showInsideHeaderOn
    )
    : null;

  if (isLoading) return <RenderLoadingCard />;
  if (effectivelyAuthenticated && hasError) return <RenderErrorCard refresh={refresh} />;

  const hasSteps = displayData?.steps && displayData?.steps?.length > 0;
  const isChallengeExpired = displayData?.challengeStatus === 'EXPIRED';
  const isPostOptIn = Boolean(
    displayData?.isOptedIn
    || displayData?.step1Pending
    || DISPLAY_PROGRESS_STATUS.includes(displayData?.activityStatus)
  );
  const showProgressContent = effectivelyAuthenticated
    && hasSteps
    && (isPostOptIn || isChallengeExpired);

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div suppressHydrationWarning ref={impressionRef} clickid={clickID}>
      {SweepstakesEntryCardHeaderProps?.showOutsideHeaderOn && (
        <div className={headerClasses.outside} data-testid="sweepstakes-header-outside">
          <RenderHeader
            href={SweepstakesEntryCardHeaderProps.href}
            onClick={SweepstakesEntryCardHeaderProps.onClick}
            openNewWindow={SweepstakesEntryCardHeaderProps.openNewWindow}
          />
        </div>
      )}
      <Card grow data-testid="sweepstakes-entry-card">
        <div className="sui-relative sui-overflow-hidden sui-rounded-lg">
          <img
            src={getImageURL('sweepstakes-field-bg.jpeg')}
            alt=""
            aria-hidden="true"
            width="600"
            height="400"
            className="sui-absolute sui-inset-0 sui-w-full sui-h-full sui-object-cover sui-object-top"
          />
          <div className="sui-relative sui-z-10 sui-p-4 sm:sui-p-6">
            {SweepstakesEntryCardHeaderProps?.showInsideHeaderOn && (
              <div className={headerClasses.inside} data-testid="sweepstakes-header-inside">
                <RenderHeader
                  href={SweepstakesEntryCardHeaderProps.href}
                  onClick={SweepstakesEntryCardHeaderProps.onClick}
                  openNewWindow={SweepstakesEntryCardHeaderProps.openNewWindow}
                />
              </div>
            )}
            <div className="sui-flex">
              <div className="sui-flex sui-gap-7 sui--mx-4 sui-px-4">
                <RenderLogo
                  logoUrl={displayData?.logo}
                  logoFallback={logoFallback}
                  imgRetryCounter={imgRetryCounter}
                />
                <div className="sui-flex-1">
                  <RenderTitle title={displayData?.title} subtitle={displayData?.challengeType} />
                  <RenderDescription description={displayData?.cardDescription} />
                  {showProgressContent && !isChallengeExpired && displayData?.expirationDate && (
                    <RenderDescription description={`Ends ${displayData.expirationDate}`} />
                  )}
                </div>
              </div>
            </div>
            <RenderCtaSection
              isAuthenticated={effectivelyAuthenticated}
              activityStatus={displayData?.activityStatus}
              isOptedIn={displayData?.isOptedIn}
              step1Pending={displayData?.step1Pending}
              terms={displayData?.termsAndConditions}
              onGetStarted={handleDetailsClick}
              isPurchaser={isPurchaser}
              onAuthClick={handleAuthClick}
              getStartedLabel={displayData?.getStartedCta}
            />
            {showProgressContent && (
              <RenderProgressSection
                sweepstakesData={displayData}
                onCompletedCta={onCompletedCta}
                isExpiredIncomplete={isChallengeExpired}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

SweepstakesEntryCard.propTypes = {
  onCompletedCta: PropTypes.func,
  impressionData: PropTypes.shape({}),
  SweepstakesEntryCardHeaderProps: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    openNewWindow: PropTypes.bool,
    showOutsideHeaderOn: PropTypes.oneOfType([
      PropTypes.oneOf(['MW', 'TW', 'DT']),
      PropTypes.arrayOf(PropTypes.oneOf(['MW', 'TW', 'DT'])),
    ]),
    showInsideHeaderOn: PropTypes.oneOfType([
      PropTypes.oneOf(['MW', 'TW', 'DT']),
      PropTypes.arrayOf(PropTypes.oneOf(['MW', 'TW', 'DT'])),
    ]),
  }),
};

SweepstakesEntryCard.defaultProps = {
  onCompletedCta: null,
  impressionData: null,
  SweepstakesEntryCardHeaderProps: null,
};
